/**
 * Enum representing various feature flags.
 * These flags can be used to enable or disable specific features within the application.
 *
 * @enum {string}
 *
 * @property {string} MAINTENANCE_MODE - Flag indicating if app is in maintenance mode.
 * @property {string} CHATBOT - Flag that enable/disabled the Chatbot by Aurora
 * @property {string} TUTORIAL - Flag that enable/disabled the Tutorial dialogs on the first view.
 * @property {string} DASHBOARD - Flag that enable/disabled the menu and all features for Dashboard.
 * @property {string} ORGANIZATION - Flag that enable/disabled the menu and all features for Organization.
 * @property {string} PEOPLE - Flag that enable/disabled the menu and all features for People.
 * @property {string} SURVEY - Flag that enable/disabled the menu and all features for Survey.
 * @property {string} ASPECT - Flag that enable/disabled the menu and all features for Aspect.
 * @property {string} SWITCH_ORGANIZATION - Flag that enable/disabled the functionality allowing the user to switch organizations.
 * @property {string} PAYMENTS - Flag that enable/disabled the Payments functionality, including sub-features like making payments, viewing payment history, etc.
 * @property {string} DEPARTMENTS - Flag that enable/disabled the menu and all features for Departments section.
 * @property {string} DEPARTMENT - Flag that enable/disabled the menu "Dipartimento" for Manager and Employee.
 * @property {string} TRAINING - Flag that enable/disabled the menu for the static page "training".
 * @property {string} PROFILE - This flag enable or disable the profile in the learning platform.
 * @property {string} CONVERSATIONS - This flag enable or disable the conversations route.
 * @property {string} CREDITS - This flag enable or disable the credits feature.
 * @property {string} QUIZ - This flag enable or disable the quiz feature.
 * @property {string} ASSESSMENT - This flag enable or disable the assessment page for manager and employee.
 * @property {string} MENULIGHT - This flag enable or disabled the arrow to open or close the light menu.
 * @property {string} LANGUAGE_SELECTOR - This flag enable or disabled the language selector component.
 * @property {string} MULTI_ORGANIZATION - This flag enable or disable the multi-organization feature.
 * @property {string} REGISTRATION - This flag enable or disable the registration functionality on the login page.
 * @property {string} REPORTS - This flag enable or disable the menu and all features for Reports.
 * @property {string} HIDE_FEATURE_AI_BOOST_INCLUDED_IN_PLAN - This flag hides the "AI-Boost" section inside the "Included in plan" details of the plan tab.
 */
export enum FeatureFlag {
  /**
   * @property {string} MAINTENANCE_MODE - maintenance-mode
   * Flag that enables or disables if the app is in maintenance mode or not. It is disabled by default.
   */
  MAINTENANCE_MODE = 'maintenanceMode',
  /**
   * @property {string} CHATBOT - chatbot
   * Flag that enable/disabled the Chatbot by Aurora
   */
  CHATBOT = 'chatbot',
  /**
   * @property {string} TUTORIAL - tutorial
   * Flag that enable/disabled the Tutorial dialogs on the first view.
   */
  TUTORIAL = 'tutorial',
  /**
   * @property {string} DASHBOARD - dashboard
   * Flag that enables or disables the display of the dashboard.
   */
  DASHBOARD = 'dashboard',
  /**
   * @property {string} PUBLIC_DOCS - public-docs
   * This flag enable or disable the banners that open the public docs.
   */
  /**
   * @property {string} ORGANIZATION - organization
   * Flag that enables or disables the organization feature.
   */
  ORGANIZATION = 'organization',
  /**
   * @property {string} DEPARTMENTS - departments
   * Flag that enables or disables the departments feature.
   */
  DEPARTMENTS = 'departments',
  /**
   * @property {string} PEOPLE - people
   * Flag that enables or disables the people feature.
   */
  PEOPLE = 'people',
  /**
   * @property {string} ASPECT - aspect
   * Flag that enables or disables the aspect feature.
   */
  ASPECT = 'aspect',
  /**
   * @property {string} SWITCH_ORGANIZATION - switch-organization
   * Flag that enables or disables the functionality allowing the user to switch organizations. It is enabled by default.
   */
  SWITCH_ORGANIZATION = 'switchOrganization',
  /**
   * @property {string} PAYMENTS - payments
   * Flag that enables or disables the Payments functionality, including sub-features like making payments, viewing payment history, etc.
   */
  PAYMENTS = 'payments',
  /**
   * @property {string} DEPARTMENT - department
   * Flag that enables or disables the menu "Dipartimento" for Manager and Employee.
   */
  DEPARTMENT = 'department',
  /**
   * @property {string} TRAINING - training
   * Flag that enables or disables the menu for the static page "training".
   */
  TRAINING = 'training',
  /**
   * @property {string} SURVEY - survey
   * Flag that enables or disables the menu for the static page "survey".
   */
  SURVEY = 'survey',
  /**
   * @property {string} PROFILE - profile
   * This flag enable or disable the profile in the learning platform.
   */
  PROFILE = 'profile',
  /**
   * @property {string} CONVERSATIONS - conversations
   * This flag enable or disable the conversations in the learning platform.
   */
  CONVERSATIONS = 'conversations',
  /**
   * @property {string} CREDITS - credits
   * This flag enable or disable the credits feature.
   */
  CREDITS = 'credits',
  /**
   * @property {string} ASSESSMENT - assessment
   * This flag enable or disable the assessment page for manager and employee.
   */
  ASSESSMENT = 'assessment',
  /**
   * @property {string} MENULIGHT - menuLight
   * This flag enable or disabled the arrow to open or close the light menu.
   */
  MENULIGHT = 'menuLight',
  /**
   * @property {string} LANGUAGE_SELECTOR - languageSelector
   * This flag enable or disabled the language selector component.
   */
  LANGUAGE_SELECTOR = 'languageSelector',
  /**
   * @property {string} MULTI_ORGANIZATION - multiOrganization
   * This flag enable or disable the multi-organization feature.
   */
  MULTI_ORGANIZATION = 'multiOrganization',
  /**
   * @property {string} REGISTRATION - registration
   * This flag enable or disable the registration functionality on the login page.
   */
  REGISTRATION = 'registration',
  /**
   * @property {string} REPORTS - reports
   * This flag enable or disable the menu and all features for Reports.
   */
  REPORTS = 'reports',
  /**
   * @property {string} HIDE_FEATURE_AI_BOOST_INCLUDED_IN_PLAN - hideFeatureAIBoostIncludedInPlan
   * Flag that hides the "AI-Boost" section inside the "Included in plan" details of the plan tab when enabled.
   */
  HIDE_FEATURE_AI_BOOST_INCLUDED_IN_PLAN = 'hideFeatureAiBoostIncludedInPlan',
}
